import { Percentage1or2DecimalsRegex } from "@fartherfinance/frontend/constants/regex/regex";

import addCommas from "./CommaHelperFunctions/addCommas";
import removeCommas from "./CommaHelperFunctions/removeCommas";

const moreThan2Decimals = (value: string | number): string | true => {
  if (typeof value !== "string") {
    return `Invalid value`;
  }

  const decimalPortion = value.split(".")[1] as string | undefined;
  if (decimalPortion === undefined) {
    return true;
  }

  // This will catch even 1.110
  return decimalPortion.length <= 2 ? true : `Only 2 decimals allowed`;
};

type FormNumberFieldRules = {
  required: boolean;
  min: {
    value: number;
    message: string;
  };
  max: {
    value: number;
    message: string;
  };
  validate: {
    moreThan2Decimals: (value: number) => boolean | string;
  };
};

interface FormNumberFieldParams {
  required: boolean;
  minValue: number;
  maxValue: number;
}

export const formNumberFieldRules = (
  { required, minValue, maxValue }: FormNumberFieldParams,
  additionalValidations?: Record<string, (value: number) => boolean | string>
): FormNumberFieldRules => ({
  required,
  min: {
    value: minValue,
    message: `Can't be less than ${minValue}`,
  },
  max: {
    value: maxValue,
    message: `Can't be greater than ${maxValue}`,
  },
  validate: { moreThan2Decimals, ...(additionalValidations || {}) },
});

export const formNumberFieldUpTo2DecimalsWithCommasCustomRegex = {
  regex: /(^[0-9,]+$)|(^[0-9,]*\.[0-9]$)|(^[0-9,]*\.[0-9][0-9]$)/g,
  message: "Invalid number, only 2 decimals max",
};

// Check if the value is a valid number
export const formNumberFieldCustomRegex = {
  regex: /^[\d]+$/g,
  message: "Must be only numerical digits",
};

export const formNumberFieldWithCommasCustomRegex = {
  regex: /(^[0]{1}$)|(^[1-9,]+[0-9,]*$)/g,
  message: "Invalid number, must be an integer",
};

export const formPercentFieldCustomRegex = {
  regex: Percentage1or2DecimalsRegex,
  message: "Must have 1 or 2 decimal places",
};

const regexAccept0 = /^[0]$/;

const combinedRegex = new RegExp(
  `${Percentage1or2DecimalsRegex.source}|${regexAccept0.source}`
);

export const formPercentFieldAccept0CustomRegex = {
  regex: combinedRegex,
  message: "Must have 1 or 2 decimal places",
};

export const formNumberFieldWithCommasRules = (
  { required, minValue, maxValue }: FormNumberFieldParams,
  additionalValidations?: Record<string, (value: string) => boolean | string>
) => ({
  required,
  validate: {
    minMaxValidationWithCommas: (value: string): string | true => {
      const numStr = removeCommas(value);
      const num = Number(numStr);

      if (isNaN(num)) {
        return "Invalid number";
      }

      if (num < minValue) {
        return `Can't be less than ${addCommas(minValue)}`;
      }

      if (num > maxValue) {
        return `Can't be greater than ${addCommas(maxValue)}`;
      }

      return true;
    },
    ...(additionalValidations || {}),
  },
});

export const downPaymentAndAmountDiff =
  (purchasePriceValue: string) => (downPaymentValue: string) => {
    const dp = Number(removeCommas(downPaymentValue));

    const pp = purchasePriceValue
      ? Number(removeCommas(purchasePriceValue))
      : 0;

    if (isNaN(dp)) {
      return "Invalid number";
    }

    if (isNaN(pp)) {
      return "Purchase price is an invalid number";
    }

    return dp < pp || "Must be less than the purchase price";
  };
